import React, { useState, useEffect, forwardRef } from "react";
import PropTypes from "prop-types";

import styles from "./ModalVideo.scss";

const ytSrc = "https://www.youtube.com/iframe_api";
let ytInserted = false;

const YoutubeVideo = forwardRef(
  ({ id, play, width, height, onReady, onPlay, onPause, children }, ref) => {
    const playerRef = ref;
    const [, setPlayOnReady] = useState(play);
    const [videoPlaying, setVideoPlaying] = useState(false);

    const playVideo = () => {
      if (!playerRef.current) {
        setPlayOnReady(true);
        return;
      }
      playerRef.current.playVideo();
      setVideoPlaying(true);
      onPlay(playerRef.current);
    };

    const pauseVideo = () => {
      if (playerRef.current) {
        playerRef.current.pauseVideo();
        setVideoPlaying(false);
        onPause(playerRef.current);
      }
    };

    const onPlayerReady = (event) => {
      playerRef.current = event.target;
      onReady(playerRef.current);
      setPlayOnReady((value) => {
        if (value && !videoPlaying) playVideo();
        return value;
      });
    };

    const initializeYtPlayer = () => {
      return new window.YT.Player(`youtube-player-${id}`, {
        width: `${width}px`,
        height: `${height}px`,
        videoId: id,
        events: {
          onReady: onPlayerReady,
        },
      });
    };

    const loadVideo = () => {
      if (playerRef.current) return;

      window?.YT?.ready(() => {
        initializeYtPlayer();
      });
    };

    const initializeScript = () => {
      if (!window.YT) {
        if (ytInserted) return;
        ytInserted = true;
        const ytScript = document.querySelector(`script[src="${ytSrc}"]`);
        if (ytScript) return;
        const newScript = document.createElement("script");
        newScript.src = ytSrc;
        const firstScriptTag = document.getElementsByTagName("script")[0];
        if (firstScriptTag) {
          firstScriptTag.parentNode.insertBefore(newScript, firstScriptTag);
          return;
        }
        document.body.appendChild(newScript);
      }
    };

    useEffect(() => {
      if (play) {
        playVideo();
      } else {
        pauseVideo();
      }
    }, [play]);

    useEffect(() => {
      initializeScript();
      loadVideo();

      return () => {
        playerRef.current = null;
      };
    }, [window.YT]);

    return (
      <div className={styles["yt-video-container"]}>
        <div id={`youtube-player-${id}`}>{children}</div>
      </div>
    );
  }
);

YoutubeVideo.propTypes = {
  id: PropTypes.string.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  play: PropTypes.bool,
  onReady: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

YoutubeVideo.defaultProps = {
  // Default dimensions are a big 16:9 scaling format to fit into max-width value of ModalVideo
  width: 2240,
  height: 1280,
  play: false,
  onReady: () => {},
  onPlay: () => {},
  onPause: () => {},
  children: null,
};

YoutubeVideo.displayName = "YoutubeVideo";

export default YoutubeVideo;
